import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  ChannelCreated,
  ChannelJoined,
  ChatType,
  CreateChannel,
  CreateNewMessage,
  JoinChannel,
  LeaveChannel,
  MessageReactedEvent,
  MessageReactedTo,
  MessagesReadEvent,
  MessagesReadIn,
  MessageSentTo,
  NewMessageSent,
  ReactToMessage,
  ReactToMessageRequest,
  SayTyping,
  SayTypingRequest,
  SendMessage,
  SomeoneTypingEvent,
  SomeoneTypingIn,
} from '@smatchy/chat-domain';

/**
 * Recette 2.2.11, 23/10/2026 (Asana 1219817449234610 and 1219821384085503):
 * the server stopped answering the chat, and the app waited for it forever.
 * « Rejoindre la discussion » did nothing at all, and a message typed in a
 * conversation left the box and went nowhere, with nothing said either time.
 * Every question the app asks the chat server now has an answer within this
 * delay, or fails with ChatUnavailableError, which the screens turn into a
 * message.
 */
export const CHAT_SERVER_ANSWER_DELAY_MS = 10_000;

/** The chat server refused, said nothing in time, or no conversation is open. */
export class ChatUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChatUnavailableError';
  }
}

/**
 * Waits for one answer to `event`, for CHAT_SERVER_ANSWER_DELAY_MS at most.
 *
 * Listening starts before the question is asked, so a fast answer is never
 * missed. An `undefined` answer is the server saying no (chats.gateway.ts
 * sends ChannelCreated with nothing when the creation fails): the listener
 * used to drop it and then wait for an answer that would never come.
 */
function theAnswerTo<T>(
  socket: Socket | undefined,
  event: string,
  ask: () => void,
  accepts: (answer: T) => boolean = () => true,
): Promise<T> {
  if (!socket) {
    return Promise.reject(new ChatUnavailableError(`${event}: the chat client is not connected`));
  }
  return new Promise<T>((resolve, reject) => {
    const settle = (outcome: () => void) => {
      clearTimeout(timer);
      socket.off(event, onAnswer);
      outcome();
    };
    const onAnswer = (answer: T | undefined) => {
      if (answer === undefined || answer === null) {
        settle(() => reject(new ChatUnavailableError(`${event}: the chat server refused`)));
      } else if (accepts(answer)) {
        settle(() => resolve(answer));
      }
    };
    const timer = setTimeout(
      () =>
        settle(() => reject(new ChatUnavailableError(`${event}: no answer within ${CHAT_SERVER_ANSWER_DELAY_MS} ms`))),
      CHAT_SERVER_ANSWER_DELAY_MS,
    );
    socket.on(event, onAnswer);
    ask();
  });
}

interface ChatSDKOptions {
  serverUrl: string;
}

export interface ChatMessage {
  id: string;
  text: string;
  sender?: string;
  timestamp?: number;
}

interface UserClient {
  userId: string;
  token: string;
}

/** What the server answers when a conversation is created or joined. */
interface AnsweredChannel {
  _id: string;
  type: ChatType;
  participantIds: string[];
  // GroupId / EventId /
  refId: string;
  initiatorId: string;
}

export class Channel {
  #socket: Socket;
  #id: string;
  #type: ChatType;
  #myUserId: string | undefined;

  get id(): string {
    return this.#id;
  }

  /**
   * The server keeps each conversation in a socket.io room, and a room does
   * not survive a reconnection (network lost, app back from the background).
   * Joining again on every reconnection keeps messages coming.
   */
  readonly #rejoin = () => {
    this.#socket.emit(JoinChannel, { id: this.#id });
  };

  constructor(
    socket: Socket,
    id: string,
    type: ChatType,
    participantIds: string[],
    initiatorId: string,
    myUserId?: string,
  ) {
    this.#id = id;
    this.#myUserId = myUserId;
    this.#type = type;
    this.#socket = socket;
    if (id && socket) {
      socket.on('connect', this.#rejoin);
    }
  }

  /**
   * Resolves once the server sends the message back to its author, which it
   * does for every message it saved, delivered or not (chats.gateway.ts). No
   * echo in time, or no conversation, and it rejects: the message was not
   * saved, and the screen gives the text back instead of losing it.
   *
   * The echo is told apart by its text and, when known, its author. Someone
   * else sending the very same words in the same second would be taken for it;
   * the alternative, an acknowledgement, needs a server change that an
   * already published API does not have.
   */
  sendMessage(text: string, replyToId?: string): Promise<NewMessageSent> {
    if (!this.#id || !this.#socket) {
      return Promise.reject(new ChatUnavailableError(`${SendMessage}: no conversation is open`));
    }
    const createNewMessage: CreateNewMessage = {
      chatId: this.#id,
      type: this.#type,
      messageType: 'text',
      messageData: {
        text,
      },
      ...(replyToId ? { replyToId } : {}),
    };
    return theAnswerTo<NewMessageSent>(
      this.#socket,
      MessageSentTo(this.#id),
      () => this.#socket.emit(SendMessage, createNewMessage),
      (echo) => echo.messageData?.text === text && (this.#myUserId === undefined || echo.sentBy === this.#myUserId),
    );
  }

  /**
   * One of my outings, as a card. Only the id leaves the phone; the API
   * refuses an outing I am not on, a private one, or one that is over, and
   * then sends nothing back, so this rejects like a message that did not leave.
   */
  shareOuting(eventId: string): Promise<NewMessageSent> {
    if (!this.#id || !this.#socket) {
      return Promise.reject(new ChatUnavailableError(`${SendMessage}: no conversation is open`));
    }
    const createNewMessage: CreateNewMessage = {
      chatId: this.#id,
      type: this.#type,
      messageType: 'outing',
      messageData: { eventId },
    };
    return theAnswerTo<NewMessageSent>(
      this.#socket,
      MessageSentTo(this.#id),
      () => this.#socket.emit(SendMessage, createNewMessage),
      (echo) =>
        (echo.messageData as { eventId?: string })?.eventId === eventId &&
        (this.#myUserId === undefined || echo.sentBy === this.#myUserId),
    );
  }

  message(): Observable<NewMessageSent> {
    console.log('subscribed to ', MessageSentTo(this.#id));
    return this.onEvent<NewMessageSent>(MessageSentTo(this.#id)).pipe(map((message: NewMessageSent) => message));
  }

  /** SMA-22: picks, replaces or takes back this person's reaction. */
  react(messageId: string, emoji: string): void {
    const request: ReactToMessageRequest = { chatId: this.#id, messageId, emoji };
    this.#socket.emit(ReactToMessage, request);
  }

  reactions(): Observable<MessageReactedEvent> {
    return this.onEvent<MessageReactedEvent>(MessageReactedTo(this.#id));
  }

  /**
   * « I am typing », with no answer expected: an API that predates it ignores
   * an event it does not know, and the screen loses nothing.
   */
  sayTyping(): void {
    if (!this.#id || !this.#socket) {
      return;
    }
    const request: SayTypingRequest = { chatId: this.#id };
    this.#socket.emit(SayTyping, request);
  }

  /** The others in this conversation who are typing, one signal at a time. */
  othersTyping(): Observable<SomeoneTypingEvent> {
    return this.onEvent<SomeoneTypingEvent>(SomeoneTypingIn(this.#id));
  }

  /**
   * Who just read which messages of this conversation, so « Vu » changes
   * while it is open. An API that predates it sends nothing, and « Vu » is
   * then only refreshed when the conversation is opened again, as before.
   */
  messagesRead(): Observable<MessagesReadEvent> {
    return this.onEvent<MessagesReadEvent>(MessagesReadIn(this.#id));
  }

  leaveChannel() {
    this.#socket.off('connect', this.#rejoin);
    this.#socket.emit(LeaveChannel, {
      id: this.id,
    });
  }

  private onEvent<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      this.#socket.on(event, (data: T) => observer.next(data));
    }).pipe(filter((data) => data !== undefined));
  }
}

export class ChatClient {
  private socket!: Socket;
  #user: UserClient | undefined;
  #url: string;
  #channel: Channel | undefined;

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  get channel(): Channel {
    return this.#channel || new Channel(this.socket, '', 'group', [], '');
  }

  constructor(options: ChatSDKOptions) {
    this.#url = options.serverUrl;
  }

  connect(user: { userId: string; token: string }) {
    const previous = this.#user;
    this.#user = user;

    // The same person again, typically after the access token was refreshed.
    // The socket is kept: an open conversation holds it, with its listeners,
    // and a new socket used to leave that conversation deaf and mute until it
    // was reopened. Only the token changes, and the socket reconnects with it;
    // the open channel then joins its room again on 'connect'.
    if (this.socket && previous?.userId === user.userId) {
      if (previous.token === user.token && this.socket.active) {
        return;
      }
      this.socket.io.opts.extraHeaders = { Authorization: `${user.token}` };
      this.socket.disconnect();
      this.socket.connect();
      return;
    }

    // Someone else, or the first connection: start from a clean socket.
    this.disconnect();

    this.socket = io(this.#url, {
      withCredentials: true,
      extraHeaders: {
        Authorization: `${user.token}`,
      },
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
  }

  createChannel(type: ChatType, id: string): Promise<Channel> {
    return theAnswerTo<AnsweredChannel>(this.socket, ChannelCreated, () =>
      this.socket.emit(CreateChannel, { name: id, type, id }),
    ).then((channel) => this.#channelFrom(channel));
  }

  joinChannel(channelId: string): Promise<Channel> {
    // The server says nothing when it refuses a join: the delay is the answer.
    return theAnswerTo<AnsweredChannel>(this.socket, ChannelJoined, () =>
      this.socket.emit(JoinChannel, { id: channelId }),
    ).then((channel) => this.#channelFrom(channel));
  }

  #channelFrom(channel: AnsweredChannel): Channel {
    return new Channel(
      this.socket,
      channel._id,
      channel.type,
      channel.participantIds,
      channel.initiatorId,
      this.#user?.userId,
    );
  }
}
